import { Script } from "gatsby"
import React, { useState } from "react"
import Col from "react-bootstrap/Col"
import Form from "react-bootstrap/Form"
import Row from "react-bootstrap/Row"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import Alert from "@atoms/Alert/Alert"
import Button from "@atoms/Button/Button"
import SelectHybrid from "@atoms/SelectHybrid/SelectHybrid"
import "@lang/i18n"
import { normalizeSelectValues } from "@utils/helpers"

import FormHandler from "../FormHandler"
import mappings from "./FormOfficeSpaceMappings"

const FormOfficeSpace = ({
  expanded = true,
  currentLanguage = "en",
  city = "",
}) => {
  const { t } = useTranslation()

  const cityFiltered = city === "Göteborg" ? "Gothenburg" : city

  const safeCities = [
    "Espoo",
    "Gothenburg",
    "Helsinki",
    "Luxembourg",
    "Oslo",
    "Oulu",
    "Stockholm",
    "Tampere",
    "Vantaa",
  ]

  const citySafe = safeCities.includes(cityFiltered) ? cityFiltered : null

  const { register, handleSubmit, errors } = useForm({
    defaultValues: {
      fc_location_of_interest: citySafe,
    },
  })

  const privacyPolicySlug = "/privacy-policy/"
  const formCaptureScript =
    "https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/eur/FormCapture/FormCapture.bundle.js"
  const formId = "form-office-space"
  const captureConfig = {
    FormId: "de66f385-0ae3-ef11-8eea-7c1e521fbcd9",
    FormApiUrl:
      "https://public-eur.mkt.dynamics.com/api/v1.0/orgs/53f42089-4f9f-4a0e-9310-dc2e00b93cd2/landingpageforms",
  }

  const siteTitle =
    typeof document !== "undefined"
      ? document.title.replace(" | Technopolis", "")
      : null
  const formUrl = typeof window !== "undefined" ? window.location?.href : null

  const [expand, setExpand] = React.useState(expanded)
  const [errorMessage, setErrorMessage] = useState()
  const [serverState, setServerState] = useState({
    submitting: false,
    status: null,
  })

  const formHandler = new FormHandler(
    "Office space contact form",
    currentLanguage,
    serverState,
    setServerState,
    errorMessage,
    setErrorMessage
  )

  const onSubmit = (data) => {
    const { submitting } = serverState
    if (!submitting) {
      formHandler.submitForm(data, formId, mappings, captureConfig)
    }
  }

  return (
    <>
      {!expand && (
        <div className="base-margin-top-xs">
          <Button
            variant="primary"
            size="medium-full-width"
            expanded={expand}
            onClick={() => setExpand(true)}
          >
            {t("Contact us")}
          </Button>
        </div>
      )}

      {expand && (
        <>
          <Script src={formCaptureScript} />
          <Form className="form" onSubmit={handleSubmit(onSubmit)} id={formId}>
            <Row className="row-minor-gutters">
              <Col md={{ span: 6 }}>
                {/* fc_firstname */}
                <Form.Group>
                  <Form.Label htmlFor="fc_firstname">
                    {t("First name")} *
                  </Form.Label>
                  <Form.Control
                    name="fc_firstname"
                    id="fc_firstname"
                    ref={register({ required: true })}
                    type="text"
                    isInvalid={!!errors.fc_firstname}
                  />
                </Form.Group>
              </Col>
              <Col md={{ span: 6 }}>
                {/* fc_lastname */}
                <Form.Group>
                  <Form.Label htmlFor="fc_lastname">
                    {t("Last name")} *
                  </Form.Label>
                  <Form.Control
                    name="fc_lastname"
                    id="fc_lastname"
                    ref={register({ required: true })}
                    type="text"
                    isInvalid={!!errors.fc_lastname}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* fc_company */}
            <Form.Group>
              <Form.Label htmlFor="fc_company">{t("Company")} *</Form.Label>
              <Form.Control
                name="fc_company"
                id="fc_company"
                ref={register({ required: true })}
                type="text"
                isInvalid={!!errors.fc_company}
              />
            </Form.Group>

            {/* fc_email */}
            <Form.Group>
              <Form.Label htmlFor="fc_email">{t("Email")} *</Form.Label>
              <Form.Control
                name="fc_email"
                id="fc_email"
                ref={register({ required: true })}
                type="email"
                isInvalid={!!errors.fc_email}
              />
            </Form.Group>

            {/* fc_phone */}
            <Form.Group>
              <Form.Label htmlFor="fc_phone">{t("Phone")} *</Form.Label>
              <Form.Control
                name="fc_phone"
                id="fc_phone"
                ref={register({ required: true })}
                type="text"
                isInvalid={!!errors.fc_phone}
              />
            </Form.Group>

            {/* fc_location_of_interest */}
            {citySafe && (
              <input
                type="hidden"
                ref={register}
                name="fc_location_of_interest"
              />
            )}
            {!citySafe && (
              <SelectHybrid
                ref={register({ required: true })}
                json={normalizeSelectValues(
                  [
                    "Espoo",
                    "Gothenburg",
                    "Helsinki",
                    "Luxembourg",
                    "Oslo",
                    "Oulu",
                    "Stockholm",
                    "Tampere",
                    "Vantaa",
                  ],
                  t
                )}
                defaultValue={t("Select")}
                isInvalid={!!errors.fc_location_of_interest}
                label={t("Location of interest") + " *"}
                name="fc_location_of_interest"
                id="fc_location_of_interest"
              />
            )}

            {/* fc_message */}
            <Form.Group>
              <Form.Label htmlFor="fc_message">
                {t("Tell us about your needs")}
              </Form.Label>
              <Form.Control
                name="fc_message"
                id="fc_message"
                ref={register}
                as="textarea"
                rows="4"
                placeholder={t(
                  "Here you can describe your needs in more detail, like the number of employees and your preferred move-in date."
                )}
              />
            </Form.Group>

            {/* fc_hearaboutus */}
            <Form.Group>
              <SelectHybrid
                ref={register({ required: true })}
                json={normalizeSelectValues(
                  [
                    "Search engine (Google, Bing, etc.)",
                    "AI-powered tools (ChatGPT, Copilot, etc.)",
                    "Social media (Facebook, LinkedIn, etc.)",
                    "Advertisement",
                    "Media (newspaper, etc.)",
                    "Office space search portal (Kauppalehti toimitilat, etc.)",
                    "Recommendation",
                    "I was contacted about our office space needs",
                    "Other",
                  ],
                  t
                )}
                defaultValue={t("Select")}
                isInvalid={!!errors.fc_hearaboutus}
                label={t("How did you hear about us?") + " *"}
                name="fc_hearaboutus"
                id="fc_hearaboutus"
              />
            </Form.Group>

            <div className="base-margin-top-xs base-margin-bottom-xs">
              {/* fc_receive_information */}
              <Form.Group controlId="fc_receive_information">
                <Form.Check
                  ref={register}
                  type="checkbox"
                  name="fc_receive_information"
                  id="fc_receive_information"
                  label={t(
                    "Yes, I want to receive important and topical information from Technopolis"
                  )}
                />
              </Form.Group>

              {/* fc_privacy_policy_terms_of_use */}
              <Form.Group controlId="fc_privacy_policy_terms_of_use">
                <Form.Check
                  required
                  ref={register({ required: true })}
                  type="checkbox"
                  name="fc_privacy_policy_terms_of_use"
                  id="fc_privacy_policy_terms_of_use"
                  label={t("I accept privacy policy and terms of use")}
                />
              </Form.Group>

              <div className="form-information base-margin-top-xs base-margin-bottom-small">
                <p>
                  <a
                    href={privacyPolicySlug}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("Get to know our privacy policy and terms of use.")}
                  </a>
                </p>
              </div>
            </div>

            <div className="base-margin-top-xs">
              <input type="hidden" name="fc_leadsource" value="Web" />
              <input type="hidden" name="fc_site_title" value={siteTitle} />
              <input type="hidden" name="fc_form_url" value={formUrl} />
              <input
                type="hidden"
                name="fc_analytics"
                value={formHandler.getAnalyticsData()}
              />
              <input type="hidden" name="fc_leadtype" value="Real estate" />
              <Button
                type="submit"
                variant="tertiary"
                size="medium-full-width"
                submitting={serverState.submitting}
              >
                {t("Submit")}
              </Button>
            </div>

            {errorMessage && (
              <div
                className="base-margin-top-xxs"
                style={{ position: "relative" }}
              >
                <Alert variant="danger" size="full-width">
                  {t(errorMessage)}
                </Alert>
              </div>
            )}
          </Form>
        </>
      )}
    </>
  )
}

export default FormOfficeSpace
